/**
 * Web Component entry point for the Community Groups widget.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/community-groups-web-component';
 *
 * // Then use in HTML:
 * // <legion-community-groups api-url="https://your-domain.com" count="6" />
 * ```
 */

import { GROUPS_THEMING_PROP_TO_PARAM } from "./types";
import {
  attachAuthBridge,
  type AuthBridgeHandle,
  AUTH_BRIDGE_OBSERVED_ATTRS,
} from "../shared/auth-bridge";

const GROUPS_CSS_ATTRIBUTES = Object.values(GROUPS_THEMING_PROP_TO_PARAM);

class LegionCommunityGroupsElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      "count",
      "layout",
      "theme",
      "width",
      "height",
      "show-view-all",
      "community-url",
      "view-all-url",
      "sort-by",
      ...GROUPS_CSS_ATTRIBUTES,

      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue === newValue) return;
    if (
      (AUTH_BRIDGE_OBSERVED_ATTRS as readonly string[]).includes(name) &&
      this.iframe
    ) {
      this.authBridge?.postCurrentToken();
      return;
    }
    this.render();
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }
  private get count(): number {
    return Math.min(
      Math.max(parseInt(this.getAttribute("count") || "6", 10), 1),
      20
    );
  }
  private get layout(): string {
    return this.getAttribute("layout") || "grid";
  }
  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }
  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }
  private get widgetHeight(): string {
    return this.getAttribute("height") || "auto";
  }
  private get showViewAll(): boolean {
    return this.getAttribute("show-view-all") !== "false";
  }
  private get communityUrl(): string {
    return this.getAttribute("community-url") || "/community";
  }
  private get viewAllUrl(): string {
    return this.getAttribute("view-all-url") || `${this.communityUrl}/groups`;
  }
  private get sortBy(): string {
    return this.getAttribute("sort-by") || "trending";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/community-groups", this.apiUrl);

    url.searchParams.set("count", String(this.count));
    url.searchParams.set("layout", this.layout);
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("showViewAll", String(this.showViewAll));
    url.searchParams.set("communityUrl", this.communityUrl);
    url.searchParams.set("viewAllUrl", this.viewAllUrl);
    url.searchParams.set("sortBy", this.sortBy);

    GROUPS_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    const resolvedHeight =
      this.widgetHeight === "auto" ? "200px" : this.widgetHeight;

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = resolvedHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Community Groups Widget";
    iframe.loading = "lazy";

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: {
            widget: "community-groups",
            count: this.count,
            theme: this.theme,
          },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load community groups widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host { display: block; width: ${this.widgetWidth.includes("%") ? this.widgetWidth : this.widgetWidth + "px"}; height: ${resolvedHeight}; }
      iframe { width: 100%; height: 100%; border: none; border-radius: 8px; }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-community-groups")
) {
  customElements.define(
    "legion-community-groups",
    LegionCommunityGroupsElement
  );
}

export { LegionCommunityGroupsElement };
